import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Package, Truck, CheckCircle2, Search, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const STATUS_LABEL = {
  booked: "Booked",
  dispatched: "Dispatched",
  in_transit: "In Transit",
  delivered: "Delivered",
  billed: "Billed",
};

const STATUS_ORDER = ["booked", "dispatched", "in_transit", "delivered", "billed"];

export default function Tracking() {
  const { tid } = useParams();
  const navigate = useNavigate();
  const [input, setInput] = useState(tid || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (tid) fetchTracking(tid);
  }, [tid]);

  const fetchTracking = async (id) => {
    setLoading(true); setError(""); setData(null);
    try {
      const { data } = await api.get(`/loads/track/${id.toUpperCase()}`);
      setData(data);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Load not found");
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    navigate(`/track/${input.trim().toUpperCase()}`);
  };

  const statusIdx = data ? STATUS_ORDER.indexOf(data.status) : -1;

  return (
    <div className="bg-white min-h-screen flex flex-col" data-testid="tracking-page">
      <Header />
      <main className="flex-1 pt-24">
        <section className="max-w-5xl mx-auto px-6 md:px-10 py-12">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-mono-label text-black/60 hover:text-[#FF4500]" data-testid="back-home-link">
            <ArrowLeft size={14}/> BACK HOME
          </Link>
          <p className="font-mono-label text-xs text-[#FF4500] mt-8">LOAD TRACKING</p>
          <h1 className="font-display font-bold text-4xl md:text-6xl mt-3 leading-[1.05]" data-testid="tracking-heading">Where&apos;s your load?</h1>

          <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="tracking-page-form">
            <input
              value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Enter load ID (e.g. ATDEMO001)"
              className="flex-1 border border-black/15 px-5 py-4 font-mono-label tracking-wider text-sm focus:outline-none focus:border-[#FF4500]"
              data-testid="tracking-page-input"
            />
            <button type="submit" className="sharp-btn bg-black text-white px-6 py-4 font-medium text-sm hover:bg-[#FF4500] inline-flex items-center gap-2" data-testid="tracking-page-submit">
              <Search size={16}/> Track
            </button>
          </form>

          {loading && (
            <div className="mt-12 flex items-center gap-3 text-black/60" data-testid="tracking-loading">
              <Loader2 className="animate-spin" size={18}/> Fetching load…
            </div>
          )}

          {error && (
            <div className="mt-12 border border-[#FF4500] bg-[#FFF5F0] p-6" data-testid="tracking-error">
              <p className="font-display font-semibold text-xl">No load found.</p>
              <p className="text-sm text-[#495057] mt-2">Double-check the load ID. If you still can&apos;t find it, call dispatch at 214-554-5884.</p>
            </div>
          )}

          {data && (
            <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-px bg-black/10 border border-black/10" data-testid="tracking-result">
              <div className="md:col-span-12 bg-black text-white p-8">
                <p className="font-mono-label text-xs text-[#FF4500]">LOAD #{data.tracking_id}</p>
                <div className="mt-4 flex flex-wrap justify-between gap-6">
                  <div>
                    <p className="font-mono-label text-[10px] text-white/50">CARRIER</p>
                    <p className="text-lg mt-1">{data.carrier_name}</p>
                  </div>
                  <div>
                    <p className="font-mono-label text-[10px] text-white/50">EQUIPMENT</p>
                    <p className="text-lg mt-1">{data.truck_type}</p>
                  </div>
                  <div>
                    <p className="font-mono-label text-[10px] text-white/50">BROKER</p>
                    <p className="text-lg mt-1">{data.broker || "—"}</p>
                  </div>
                  <div>
                    <p className="font-mono-label text-[10px] text-white/50">RATE</p>
                    <p className="text-lg mt-1">{data.rate || "—"}</p>
                  </div>
                  <div>
                    <p className="font-mono-label text-[10px] text-white/50">STATUS</p>
                    <span className={`inline-block mt-1 px-3 py-1 text-xs font-medium status-${data.status}`} data-testid="tracking-status">
                      {STATUS_LABEL[data.status]}
                    </span>
                  </div>
                </div>
              </div>

              <div className="md:col-span-6 bg-white p-8">
                <p className="font-mono-label text-[10px] text-black/50">PICKUP</p>
                <div className="flex items-start gap-3 mt-2"><MapPin size={20} className="text-[#FF4500] mt-1"/><p className="font-display text-2xl">{data.origin}</p></div>
                {data.pickup_date && <p className="text-xs text-black/50 mt-2 font-mono-label">SCHED · {data.pickup_date}</p>}
              </div>
              <div className="md:col-span-6 bg-white p-8">
                <p className="font-mono-label text-[10px] text-black/50">DELIVERY</p>
                <div className="flex items-start gap-3 mt-2"><MapPin size={20} className="text-[#FF4500] mt-1"/><p className="font-display text-2xl">{data.destination}</p></div>
                {data.delivery_date && <p className="text-xs text-black/50 mt-2 font-mono-label">SCHED · {data.delivery_date}</p>}
              </div>

              <div className="md:col-span-12 bg-white p-8">
                <p className="font-mono-label text-xs text-black/50 mb-6">PROGRESS</p>
                <div className="grid grid-cols-5 gap-2 mb-10">
                  {STATUS_ORDER.map((s, i) => (
                    <div key={s} className="flex flex-col items-center text-center">
                      <div className={`w-8 h-8 flex items-center justify-center border ${i <= statusIdx ? "bg-[#FF4500] border-[#FF4500] text-white" : "border-black/20 text-black/30"}`}>
                        {i < statusIdx ? <CheckCircle2 size={16}/> : i === statusIdx ? <Truck size={16}/> : <Package size={14}/>}
                      </div>
                      <p className={`font-mono-label text-[9px] mt-2 ${i <= statusIdx ? "text-black" : "text-black/30"}`}>{STATUS_LABEL[s].toUpperCase()}</p>
                    </div>
                  ))}
                </div>

                <p className="font-mono-label text-xs text-black/50 mb-4">EVENT LOG</p>
                <ul className="space-y-4" data-testid="tracking-events">
                  {[...data.events].reverse().map((e, i) => (
                    <li key={i} className="border-l-2 border-[#FF4500] pl-4">
                      <p className="font-medium text-sm">{STATUS_LABEL[e.status] || e.status}</p>
                      <p className="text-xs text-[#495057] mt-1">{e.note}</p>
                      <p className="font-mono-label text-[10px] text-black/40 mt-1">{new Date(e.timestamp).toLocaleString()}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
